import {
  booleanAttribute,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  ViewChild,
  computed,
  contentChildren,
  effect,
  inject,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatSidenav } from '@angular/material/sidenav';
import { MaterialModule } from '@fullyops/shared/material.module';
import { I18NextCapPipe } from 'angular-i18next';
import { FormSectionComponent } from '@fullyops/shared/components/form-section/form-section.component';

/** Narrower than this, the index moves from a rail beside the sections into a drawer. */
export const COMPACT_WIDTH = 720;

/**
 * Lays out a form's `fo-form-section`s one under the other, all open, with an index of them that follows the
 * section being read and jumps to the one picked. Whether the index is a rail or a drawer depends on the
 * form's own width rather than the screen's, so a form in a side panel gets the drawer too.
 */
@Component({
  selector: 'fo-sectioned-form',
  templateUrl: './sectioned-form.component.html',
  styleUrl: './sectioned-form.component.scss',
  imports: [MaterialModule, NgTemplateOutlet, I18NextCapPipe],
})
export class SectionedFormComponent {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input({ transform: booleanAttribute }) showIndex = true;

  protected sections = contentChildren(FormSectionComponent);
  protected compact = signal(false);
  protected activeId = signal<string | null>(null);
  protected activeTitle = computed(
    () =>
      this.sections().find((s) => s.sectionId === this.activeId())?.title ??
      this.sections()[0]?.title ??
      '',
  );

  @ViewChild(MatSidenav) private drawer?: MatSidenav;

  private toolbarHeight = signal(0);

  /** The sections in the reading band, near the top of the viewport. */
  private inView = new Set<Element>();

  constructor() {
    // The toolbar, coming before the form, publishes its height first.
    const resize = new ResizeObserver(([entry]) => {
      this.compact.set(entry.contentRect.width < COMPACT_WIDTH);
      this.toolbarHeight.set(
        parseFloat(
          getComputedStyle(this.host.nativeElement).getPropertyValue(
            '--fo-toolbar-height',
          ),
        ) || 0,
      );
    });
    resize.observe(this.host.nativeElement);

    let intersection: IntersectionObserver | undefined;
    const onIntersection = (entries: IntersectionObserverEntry[]) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          this.inView.add(e.target);
        } else {
          this.inView.delete(e.target);
        }
      }
      const first = this.sections().find((s) =>
        this.inView.has(s.element.nativeElement),
      );
      if (first != null) {
        this.activeId.set(first.sectionId);
      }
    };
    effect(() => {
      intersection?.disconnect();
      this.inView.clear();
      // What is under the page's sticky toolbar isn't being read.
      intersection = new IntersectionObserver(onIntersection, {
        rootMargin: `-${this.toolbarHeight()}px 0px -60% 0px`,
      });
      for (const s of this.sections()) {
        intersection.observe(s.element.nativeElement);
      }
    });

    inject(DestroyRef).onDestroy(() => {
      resize.disconnect();
      intersection?.disconnect();
    });
  }

  scrollTo(sectionId: string): void {
    const section = this.sections().find((s) => s.sectionId === sectionId);
    if (section == null || section.locked) {
      return;
    }
    this.activeId.set(sectionId);
    this.drawer?.close();
    const el = section.element.nativeElement;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.querySelector<HTMLElement>('.title')?.focus({ preventScroll: true });
  }

  protected openIndex(): void {
    this.drawer?.open();
  }
}
